import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef
} from 'react';

import PropTypes from 'prop-types';
import RichSelectField from '../../components/formfields/RichSelectField';
import { useTheme } from '../../context/ThemeContext'; // Import the theme hook
import { ShippingAddress } from '../../constants/types';
import useCruiseData from '../../hooks/useCruiseData'; // Import the React Query hook
import clsx from 'clsx';
import { omit } from 'lodash';
import { useQueryClient } from 'react-query';
import { useFormStore } from '../../stores/formData';
import { getAvailablePorts } from '../../utils/cruise';

const LegCruiseSelection = ({ 
  legIndex,
  selectionType,
  address: cruiseAddress,
  cruiseSelection,
  onCruiseToggle
}) => {
  // Use the theme from context instead of prop
  const { theme } = useTheme();
  const [cruiseError, setCruiseError] = useState(null);
  const resetInProgress = useRef(false);
  const lastProcessedAddress = useRef(null);
  const processedPartnerDataRef = useRef(null);

  // Zustand store to track processed partners
  const markPartnerProcessed = useFormStore(state => state.markPartnerProcessed);
  
  // Get the cruise data hook
  const { useCruiseDataByPartnerId, refreshCruiseDataByPartnerId } = useCruiseData();
  
  const actions = useFormStore((state) => state.actions);
  const helpers = useFormStore((state) => state.helpers);
  const currentLeg = useFormStore((state) => state.formData.legs[legIndex]);
  const cruiseData = useFormStore((state) => state.formData.cruiseData);

  const queryClient = useQueryClient();

  // Use React Query to fetch partner-specific cruise data
  const { 
    data: partnerCruiseData, 
    isLoading: partnerDataLoading, 
    error: partnerDataError,
  } = useCruiseDataByPartnerId(currentLeg?.selectedPartnerId, {
    enabled: currentLeg?.selectedPartnerId?.length > 0, // Only fetch when we have a partner ID
  });

  // Derive availablePorts from store instead of local state
  const availablePorts = useMemo(() => {
    // Always trust the store first
    if (currentLeg?.availablePorts?.length > 0) {
      // Validate the cached ports match the current address
      const cachedPortCountry = currentLeg.availablePorts[0]?.country_code || currentLeg.availablePorts[0]?.country;
      const currentAddressCountry = cruiseAddress?.country_code || cruiseAddress?.country;

      // Only reuse cached ports if they match the current address's country
      if (cachedPortCountry === currentAddressCountry) {
        return currentLeg.availablePorts;
      }
      // Otherwise, fall through to recalculate
    }
    
    // Only compute if not in store
    if (!currentLeg?.selectedCruiseLine?.id) return [];
    // only compute if we are the selected shippoint
    if (!currentLeg?.cruiseSelection?.[selectionType]) {
      return [];
    }
    const cruiseLineData = helpers.getCruiseLineData(currentLeg.selectedCruiseLine.id);
    if (!cruiseLineData?.cruise_sessions?.length) return [];
    
    return getAvailablePorts(cruiseLineData, cruiseAddress);
  }, [
    currentLeg?.availablePorts,
    currentLeg?.selectedCruiseLine?.id,
    cruiseAddress?.place_id,
    cruiseAddress?.country_code,
    helpers
  ]);

  // Helper function to compare address objects for meaningful changes
  const hasAddressChanged = useCallback((newAddress, oldAddress) => {
    if (!oldAddress) return !!newAddress;
    if (!newAddress) return !!oldAddress;
    
    // Compare key address fields that matter for cruise selection
    const compareFields = ['place_id', 'country_code', 'city', 'formatted_address'];
    return compareFields.some(field => newAddress[field] !== oldAddress[field]);
  }, []);

  const resetCruiseFields = useCallback(() => {
    // Enhanced protection against race conditions
    if (resetInProgress.current) return;
    
    const leg = helpers.getLeg(legIndex);
    
    if (leg?.cruiseSelection?.[selectionType]) {
      return; // still selected, do not reset
    }

    if (!cruiseAddress?.place_id) {
      return; // no valid address to compare
    }

    // Check if the address actually changed (not just reference)
    if (!hasAddressChanged(cruiseAddress, lastProcessedAddress.current)) {
      return; // Same address data, no need to reset
    }

    resetInProgress.current = true;
    lastProcessedAddress.current = { ...cruiseAddress }; // Store copy of current address
    
    const legAddress = leg.fromAddress?.place_id == cruiseAddress?.place_id ? 'fromAddress' : 'toAddress';
    const newAddress = {
      ...omit(leg[legAddress], ['cruise', 'cruiseSelection', 'cruiseLine', 'cruisePort', 'availablePorts']),
    };
    
    actions.leg.updateFields(legIndex, {
      [legAddress]: newAddress,
    });
    
    setCruiseError(null);
    
    // Use setTimeout to prevent immediate re-triggering
    setTimeout(() => {
      resetInProgress.current = false;
    }, 200);
  }, [
    legIndex,
    cruiseAddress?.place_id,
    cruiseAddress?.country_code,
    cruiseSelection,
    selectionType,
    hasAddressChanged,
    helpers,
    actions
  ]);

  // Consolidated useEffect with debouncing to prevent race conditions
  useEffect(() => {
    // Only trigger reset if conditions are met and we're not already resetting
    const shouldReset = cruiseAddress && 
                       (!cruiseSelection || !cruiseAddress?.cruise) && 
                       !resetInProgress.current;

    if (shouldReset) {
      // Debounce the reset to prevent rapid successive calls
      const timer = setTimeout(() => {
        resetCruiseFields();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [cruiseAddress, cruiseSelection, resetCruiseFields]);

  // Effect to recalculate ports when address changes
  useEffect(() => {
    if (!currentLeg?.selectedCruiseLine?.id) return;
    if (!cruiseAddress?.place_id) return;
    
    // If we have stored ports but the address changed, recalculate
    if (currentLeg?.availablePorts?.length > 0) {
      const cruiseLineData = helpers.getCruiseLineData(currentLeg.selectedCruiseLine.id);
      if (cruiseLineData?.cruise_sessions?.length > 0) {
        const newPorts = getAvailablePorts(cruiseLineData, cruiseAddress);
        
        // Only update if ports actually changed
        const currentPortsStr = JSON.stringify(currentLeg.availablePorts.map(p => p.id).sort());
        const newPortsStr = JSON.stringify(newPorts.map(p => p.id).sort());
        
        if (currentPortsStr !== newPortsStr) {
          actions.leg.updateField(legIndex, 'availablePorts', newPorts);
        }
      }
    }
  }, [
    cruiseAddress?.place_id,
    cruiseAddress?.country_code,
    currentLeg?.selectedCruiseLine?.id,
    legIndex,
    helpers,
    actions
  ]);

  const handleCruiseSelect = useCallback((selection) => {
    const getCruiseDataForSelection = async (selection) => {
      await queryClient.cancelQueries(['cruiseData']);
      
      onCruiseToggle?.(selectionType, selection);
      
      // Reset cruise fields when unchecking
      if (!selection) {
        actions.leg.updateFields(legIndex, {
          availableCruiseLines: [],
          selectedCruiseLine: null,
          selectedPort: null,
          selectedPartnerId: null,
          availablePorts: []
        });
        return;
      }

      const leg = helpers.getLeg(legIndex) || {};
      if (!leg.availableCruiseLines?.length) {
        const cruiseData = helpers.getCruiseData();
        if (!cruiseData || !Array.isArray(cruiseData) || cruiseData.length === 0) {
          setCruiseError('No cruise data available');
          return;
        }
        const cruiseLineOptions = cruiseData.map((cruise) => ({
          id: cruise.id,
          partner_id: cruise.partner_id,
          name: cruise.name
        }));
        actions.leg.updateFields(legIndex, {
          availableCruiseLines: cruiseLineOptions,
          selectedCruiseLine: null,
          selectedPort: null,
          selectedPartnerId: null,
          availablePorts: []
        });
      }
    };
    getCruiseDataForSelection(selection);
  }, [helpers, legIndex, selectionType, queryClient, onCruiseToggle, actions]);

  // Handle cruise line selection and trigger partner data fetch
  const handleCruiseLineSelect = useCallback((cruiseLineId) => {
    // when the cruise line is selected, we need to fetch partner-specific data if available
    if(!cruiseLineId) return;

    const leg = helpers.getLeg(legIndex) || {};
    if(!leg.availableCruiseLines?.length) return;

    const selectedCruiseLine = leg.availableCruiseLines?.find(line => line.id === cruiseLineId);
    actions.leg.updateField(legIndex, 'selectedCruiseLine', selectedCruiseLine);

    // if we have cruise sessions already, no need to refetch
    const cruiseLine = helpers.getCruiseLineData(selectedCruiseLine?.id);
    if(cruiseLine?.cruise_sessions?.length > 0) {
      const ports = getAvailablePorts(cruiseLine, cruiseAddress);
      actions.leg.updateField(legIndex, 'availablePorts', ports); // Store in leg
      return;
    } 

    // If we have a partner_id, use it to fetch partner-specific cruise data
    if (selectedCruiseLine?.partner_id) {
      refreshCruiseDataByPartnerId(selectedCruiseLine.partner_id);
      actions.leg.updateField(legIndex, 'selectedPartnerId', selectedCruiseLine.partner_id);
    } else {
      actions.leg.updateField(legIndex, 'selectedPartnerId', null);
    }
  }, [legIndex, cruiseAddress?.place_id, cruiseAddress?.country_code, helpers, actions, refreshCruiseDataByPartnerId]);

  // Handle when partner cruise data is loaded
  useEffect(() => {
    const leg = helpers.getLeg(legIndex) || {};
    if (leg.selectedCruiseLine?.id && partnerCruiseData && !partnerDataLoading && !partnerDataError) {
      
      // Prevent processing the same data multiple times
      const dataKey = `${leg.selectedPartnerId}-${leg.selectedCruiseLine.id}`;
      if (processedPartnerDataRef.current === dataKey) return;
      
      processedPartnerDataRef.current = dataKey;
      markPartnerProcessed(leg.selectedPartnerId, leg.selectedCruiseLine.id);
      
      const selectedCruiseLine = leg.selectedCruiseLine;

      // Find the cruise line data from the partner response
      // this is the api data
      const cruiseLine = partnerCruiseData.find(line => line.id === selectedCruiseLine.id);
      
      if (cruiseLine) {
        actions.cruise.updateCruiseData(cruiseLine.id, cruiseLine);
        
        const firstSession = cruiseLine.cruise_sessions?.[0];
        if (firstSession && firstSession.tour_name && firstSession.tour_name !== '')
          actions.leg.updateField(legIndex, 'cruiseFilter', 'tour_name');
        else
          actions.leg.updateField(legIndex, 'cruiseFilter', 'ship_name');

        const ports = getAvailablePorts(cruiseLine, cruiseAddress);
        actions.leg.updateField(legIndex, 'availablePorts', ports); // Store in leg
      }
    }
  }, [
    partnerCruiseData,
    partnerDataLoading,
    partnerDataError,
    legIndex,
    cruiseAddress?.place_id,
    cruiseAddress?.country_code,
    helpers,
    actions,
    markPartnerProcessed
  ]);

  useEffect(() => {
    // Auto-select cruise line if there's only one option
    const leg = helpers.getLeg(legIndex);
    
    // Only auto-select if:
    // 1. Cruise is selected
    // 2. We have exactly one cruise line available
    // 3. No cruise line is currently selected
    // 4. Not currently loading partner data
    if (
      cruiseSelection &&
      leg?.availableCruiseLines?.length === 1 &&
      !leg?.selectedCruiseLine?.id &&
      !partnerDataLoading
    ) {
      const singleCruiseLine = leg.availableCruiseLines[0];
      
      // Check if we already have cruise sessions for this line
      const cruiseLineData = helpers.getCruiseLineData(singleCruiseLine.id);
      
      if (cruiseLineData?.cruise_sessions?.length > 0) {
        // We have the data, select it immediately
        actions.leg.updateField(legIndex, 'selectedCruiseLine', singleCruiseLine);
        const ports = getAvailablePorts(cruiseLineData, cruiseAddress);
        actions.leg.updateField(legIndex, 'availablePorts', ports);
      } else if (singleCruiseLine.partner_id) {
        // We need to fetch partner data first
        actions.leg.updateField(legIndex, 'selectedCruiseLine', singleCruiseLine);
        actions.leg.updateField(legIndex, 'selectedPartnerId', singleCruiseLine.partner_id);
        refreshCruiseDataByPartnerId(singleCruiseLine.partner_id);
      } else {
        // No partner_id, just select it
        actions.leg.updateField(legIndex, 'selectedCruiseLine', singleCruiseLine);
      }
    }
  }, [
    cruiseSelection,
    currentLeg?.availableCruiseLines?.length,
    currentLeg?.selectedCruiseLine?.id,
    partnerDataLoading,
    legIndex,
    cruiseAddress?.place_id,
    cruiseAddress?.country_code,
    helpers,
    actions,
    refreshCruiseDataByPartnerId
  ]);

  const handlePortSelect = useCallback((port) => {
    const leg = helpers.getLeg(legIndex);
    if(!leg) return;
    
    // when the port is selected then we update the settings on the
    actions.leg.updateField(legIndex, 'selectedPort', port);

    if (!leg.selectedCruiseLine) return;

    const cruiseLineData = helpers.getCruiseLineData(leg.selectedCruiseLine?.id);
    if(!cruiseLineData) return;

    // determine if this is pickup or delivery
    // move the cruise filter onto the address
    const cruiseFilter = cruiseLineData?.cruise_sessions?.[0]?.tour_name?.length > 0 ? 'tour_name' : 'ship_name';
    const legAddress = leg?.fromAddress?.place_id == cruiseAddress?.place_id ? 'fromAddress' : 'toAddress';
    const otherAddress = legAddress == 'fromAddress' ? 'toAddress' : 'fromAddress';
    const newAddressUpdate = {
      ...leg[legAddress],
      cruise: true,
      cruiseSelection: selectionType,
      cruiseLine: leg.selectedCruiseLine,
      cruiseFilter: cruiseFilter,
      cruisePort: port
    };
    const otherAddressUpdate = {
      ...omit(leg[otherAddress],'cruise','cruiseSelection','cruiseLine','cruisePort'),
    };
    actions.leg.updateFields(legIndex, {
      [legAddress]: newAddressUpdate,
      [otherAddress]: otherAddressUpdate
    });
  }, [legIndex, cruiseAddress?.place_id, selectionType, helpers, actions]);

  // Theme-based styling
  const isDark = theme === 'dark';

  const checkboxClassName = clsx(
    'rounded w-[18px] h-[18px]',
    {
      'border-slate-600 bg-slate-700 text-blue-500': isDark,
      'border-gray-300 bg-white text-blue-500': !isDark
    }
  );

  const checkboxLabelClassName = clsx(
    'text-sm',
    {
      'text-slate-300': isDark,
      'text-gray-600': !isDark
    }
  );

  const fieldLabelClassName = clsx(
    'text-sm mb-2',
    {
      'text-slate-300': isDark,
      'text-gray-600': !isDark
    }
  );

  const selectionText = useMemo(() => {
    let cruiseLineName = '';
    if (cruiseData && cruiseData.length == 1) {
      // single cruise scenario
      cruiseLineName = cruiseData?.[0]?.club_name;
    }
    const cruiseName = cruiseLineName || 'a cruise';
    if (selectionType === 'pickup') {
      return `Pickup from ${cruiseName}`;
    }
    return `Deliver to ${cruiseName}`;
  }, [cruiseData?.[0]?.name, selectionType]);

  // Show loading state when fetching partner data
  const isLoadingPartnerData = partnerDataLoading;

  // Combine errors from both sources
  const displayError = cruiseError || (partnerDataError ? 'Failed to load cruise data' : null);

  const availableCruiseLines = currentLeg?.availableCruiseLines || [];
  const selectedCruiseLine = currentLeg?.selectedCruiseLine || null;
  
  // Validate that the selected port is still in available ports
  const validatedPortValue = useMemo(() => {
    const portId = cruiseAddress?.cruisePort?.id;
    if (!portId) return '';
    
    // Only show the port value if it's actually in the available ports list
    const isValidPort = availablePorts.find(p => p.id === portId);
    return isValidPort ? portId : '';
  }, [cruiseAddress?.cruisePort?.id, availablePorts]);
  
  return (
    <div className="w-full flex flex-col items-start gap-2">
      <div className="flex flex-row items-center gap-2">
        <input
          data-testid={`leg${legIndex}-cruise-checkbox-${selectionType}`}
          type="checkbox"
          checked={cruiseSelection}
          onChange={(e) => handleCruiseSelect(e.target.checked)}
          className={checkboxClassName}
        />
        <span className={checkboxLabelClassName}>
          {selectionText}
        </span>
      </div>
      {cruiseSelection && displayError && <div className="text-red-500">{displayError}</div>}
      {cruiseSelection && availableCruiseLines?.length > 1 && (
        <div className="w-full mt-2 relative flex flex-col items-start">
          <label className={fieldLabelClassName}>Cruise line</label>
          <RichSelectField
            id={`leg${legIndex}-${selectionType}-cruiseLine`}
            value={selectedCruiseLine?.id || ''}
            options={availableCruiseLines}
            useCustomDropdown={true}
            valueKey="id"
            placeholder="Select cruise line"
            formatValue={(option) => ({
                key: option?.id,
                icon: null,
                label: option?.name || '',
                value: option?.id
            })}
            onChange={(event) => {
              const { value } = event.target;
              handleCruiseLineSelect(value);
            }}
            containerClassName="w-full"
            disabled={isLoadingPartnerData}
          />
          {isLoadingPartnerData && (
            <div className="text-sm text-gray-500 mt-1">Loading cruise data...</div>
          )}
          {selectionType === 'delivery' && availableCruiseLines?.length > 1 &&
            availablePorts?.length === 0 && !isLoadingPartnerData && (
            <div className="text-sm text-gray-500 mt-1">
              {'Don\'t see your cruise line? Call or text our cruise concierge team to book at (855)-792-7529.'}
            </div>
          )}
        </div>
      )}
      {cruiseSelection &&
        selectedCruiseLine?.id &&
        !availablePorts?.length &&
        !isLoadingPartnerData && (
          <div className="text-red-500">
            No available ports for {selectedCruiseLine?.name} in{' '}
            {cruiseAddress?.country || 'the selected location'}.
          </div>
        )}
      {cruiseSelection && availableCruiseLines?.length > 0 && availablePorts?.length > 0 && (
        <div className="w-full mt-2 relative flex flex-col items-start">
          <label className={fieldLabelClassName}>Port</label>
          <RichSelectField
            id={`leg${legIndex}-${selectionType}-cruisePort`}
            value={validatedPortValue}
            options={availablePorts}
            useCustomDropdown={true}
            valueKey="id"
            placeholder="Select cruise port"
            formatValue={(option) => ({
                key: option?.id,
                icon: null,
                label: option?.name || '',
                value: option?.id
            })}
            onChange={(event) => {
              const { value } = event.target;
              const selectedPort = availablePorts.find(port => port.id === value);
              handlePortSelect(selectedPort);
            }}
            containerClassName="w-full"
          />
        </div>
      )}
    </div>
  );
};

LegCruiseSelection.propTypes = {
  legIndex: PropTypes.number.isRequired,
  selectionType: PropTypes.string.isRequired,
  address: ShippingAddress.isRequired,
  onCruiseToggle: PropTypes.func.isRequired,
  cruiseSelection: PropTypes.bool.isRequired
};

export default LegCruiseSelection;